import React, { createContext, useContext, useState, useEffect } from 'react';
import { authService, User, LoginCredentials } from '../services/authService';

interface AuthContextType {
    user: User | null;
    loading: boolean;
    error: string | null;
    login: (credentials: LoginCredentials) => Promise<void>;
    loginWithGoogle: (token: string) => Promise<void>;
    loginWithMicrosoft: (token: string) => Promise<void>;
    logout: () => void;
    isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const restoreAuthState = async () => {
            try {
                if (authService.isAuthenticated()) {
                    const currentUser = await authService.getCurrentUser();
                    setUser(currentUser);
                }
            } catch (err) {
                setError(err instanceof Error ? err.message : 'Failed to restore auth state');
            } finally {
                setLoading(false);
            }
        };

        restoreAuthState();
    }, []);

    const login = async (credentials: LoginCredentials) => {
        try {
            setLoading(true);
            setError(null);
            console.log('AuthContext: Starting login...');
            const response = await authService.login(credentials);
            console.log('AuthContext: Login successful, setting user:', response.user);
            setUser(response.user);
            console.log('AuthContext: User set, login complete');
        } catch (err) {
            console.log('AuthContext: Login failed:', err);
            setError(err instanceof Error ? err.message : 'Login failed');
            throw err;
        } finally {
            setLoading(false);
        }
    };

    const loginWithGoogle = async (token: string) => {
        try {
            setLoading(true);
            setError(null);
            const response = await authService.loginWithGoogle(token);
            setUser(response.user);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Google login failed');
            throw err;
        } finally {
            setLoading(false);
        }
    };

    const loginWithMicrosoft = async (token: string) => {
        try {
            setLoading(true);
            setError(null);
            const response = await authService.loginWithMicrosoft(token);
            setUser(response.user);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Microsoft login failed');
            throw err;
        } finally {
            setLoading(false);
        }
    };

    const logout = () => {
        authService.logout();
        setUser(null);
        setError(null);
    };

    const value = {
        user,
        loading,
        error,
        login,
        loginWithGoogle,
        loginWithMicrosoft,
        logout,
        isAuthenticated: !!user
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}; 